import cx from 'classnames';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ParticipantOnlineStatusIcon, Icon, OnlineStatusIcon, ParticipantPlatform} from 'components';
import {AgentSession, AnyParticipant, OnlineSession} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {OnlineStatus, ParticipantRole} from 'types';
import {userAuthUtils} from 'utils';
import {getParticipantName} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.COMMON.SESSION;

type Props = {
    session: AgentSession | OnlineSession;
    participant?: AnyParticipant;
    nameInBold?: boolean;
}

export const SessionParticipant = ({session, participant, nameInBold}: Props) => {
    const {t} = useTranslation();

    const localUser = useMemo(() => userAuthUtils.getLocalUser(), []);

    if (!participant) {
        return null;
    } else if (participant.role === ParticipantRole.Client) {
        return (
            <div className='tw-flex tw-items-center tw-justify-between tw-w-full tw-my-1 tw-px-2'>
                <div className='tw-flex tw-items-center'>
                    <ParticipantOnlineStatusIcon
                        participant={participant}
                        session={session}
                        reverseAlign
                    />
                    <Icon name='client-participant' className='tw-mr-2' />
                    <div className={cx('tw-text-default', {'tw-ts-base-bold': nameInBold})}>
                        {getParticipantName(participant?.data?.userInfo, t(T_KEYS.LABELS.CLIENT))}
                    </div>
                </div>
                <div>
                    <ParticipantPlatform participant={participant} />
                </div>
            </div>
        );
    } else if (participant.role === ParticipantRole.Guest) {
        return (
            <div className='tw-flex tw-items-center tw-justify-between tw-w-full tw-my-1 tw-px-2'>
                <div className='tw-flex tw-items-center'>
                    <ParticipantOnlineStatusIcon
                        participant={participant}
                        session={session}
                        reverseAlign
                    />
                    <Icon name='guest-participant' className='tw-mr-2' />
                    <div className={cx('tw-text-default', {'tw-ts-base-bold': nameInBold})}>
                        {getParticipantName(participant?.data?.userInfo, t(T_KEYS.LABELS.GUEST))}
                    </div>
                </div>
                <div>
                    <ParticipantPlatform participant={participant} />
                </div>
            </div>
        );
    } else if (participant.role === ParticipantRole.Agent) {
        const isMe = participant.data?.eid === localUser.eid;
        const name = getParticipantName(participant?.data, t(T_KEYS.LABELS.AGENT));
        return (
            <div className='tw-flex tw-items-center tw-justify-between tw-w-full tw-my-1 tw-px-2'>
                <div className='tw-flex tw-items-center'>
                    {isMe ? <OnlineStatusIcon
                        status={OnlineStatus.Online}
                        reverseAlign
                    /> : <ParticipantOnlineStatusIcon
                        participant={participant}
                        session={session}
                        reverseAlign
                    />}
                    <Icon name='agent-participant' className='tw-mr-2' />
                    <div className={cx('tw-text-default', {'tw-ts-base-bold': nameInBold})}>
                        {name}
                        {isMe && <span>{` (${t(T_KEYS.LABELS.ME)})`}</span>}
                    </div>
                </div>
                <div>
                    <ParticipantPlatform participant={participant} local={isMe} />
                </div>
            </div>
        );
    } else if (participant.role === ParticipantRole.Assistant) {
        const isMe = participant.data?.eid === localUser.eid;
        const name = getParticipantName(participant?.data, t(T_KEYS.LABELS.ASSISTANT));
        return (
            <div className='tw-flex tw-items-center tw-justify-between tw-w-full tw-my-1 tw-px-2'>
                <div className='tw-flex tw-items-center'>
                    {isMe ? <OnlineStatusIcon
                        status={OnlineStatus.Online}
                        reverseAlign
                    /> : <ParticipantOnlineStatusIcon
                        participant={participant}
                        session={session}
                        reverseAlign
                    />}
                    <Icon name='assistant-participant' className='tw-mr-2' />
                    <div className={cx('tw-text-default', {'tw-ts-base-bold': nameInBold})}>
                        {name}
                        {isMe && <span>{` (${t(T_KEYS.LABELS.ME)})`}</span>}
                    </div>
                </div>
                <div>
                    <ParticipantPlatform participant={participant} local={isMe} />
                </div>
            </div>
        );
    } else {
        return null;
    }
};